import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import classnames from 'classnames';
import { makeStyles, FontSizes, FontWeights } from '@fluentui/react';
import { List, Text, Icon } from '@fluentui/react';

import { ListItem } from 'src/components/base/ListItem';

import { runShortAction } from 'src/services/utils';
import { scheduleToLocal, SCHEDULE_HOURLY, SCHEDULE_DAILY, SCHEDULE_MONTHLY, SCHEDULE_MANUALLY } from 'shared/services/scheduler';
import { Type } from 'shared/utils/flow';

const useCustomStyles = makeStyles(({ palette }) => ({
  logout: {
    color: palette.themePrimary,
    borderTop: '1px solid #F5F5F5',
    margin: '20px 16px 0px 16px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: '0px 16px 0px 16px',  
    height: 60,
    minHeight: 60,  
    color: '#666666',
    textDecoration: 'none',

    ':hover': {
      backgroundColor: 'lightgrey',
    },
  },
  addRow: {
    '& i': {
      color: palette.themePrimary,
      margin: '0 8px 0 0',
    },
    '& span': {
      fontSize: FontSizes.medium,
      fontWeight: FontWeights.light,
    },
  },
  editRow: {
    'div:first-child': {
      width: '100%',
    },
  },
  temp: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
}))

const ActionList = ({ userStore, dataStore, flowId }) => {
  const classNames = useCustomStyles();
  const { profile = {}} = dataStore.scheduler;

  const [actions, setActions] = useState([]);

  useEffect(() => {
    updateActions();
    return reaction(
      () => ({ actions: dataStore.actions }),
      () => updateActions(),
    );
  }, []);

  function updateActions() {
    // setActions(dataStore.actions.map(x => ({ ...x, name: x.flow_id
    //   ? dataStore.getFlow(x.flow_id)?.name ?? 'Flow is removed'
    //   : x.action === Type.Pull ? 'Pull Data' : 'Push Data'
    // })));

    setActions(dataStore.actions.reduce((out, x) => {
      if (!flowId || flowId === x.flow_id) {
        const flow = { ...x, name: x.flow_id
          ? dataStore.getFlow(x.flow_id)?.name ?? 'Flow is removed'
          : x.action === Type.Pull ? 'Pull Data' : 'Push Data'
        }
        out.push(flow);
      }
      return out;
    }, []));

  }

  async function handleLogout(event) {
    event.preventDefault();
    await runShortAction(async () => {
      await dataStore.logoutSchedulerDocument();
    });
  }

  return (
    <div className={classNames.temp}>
      <List
        items={[{}, ...actions]}
        onRenderCell={(item, index) => index === 0
          ? <AddActionCell flowId={flowId} />
          : <ActionCell owner={userStore.user} action={item} flowId={flowId}/>}
      />
      <div className={classNames.logout}>
        <Text variant="small">{profile.email} <Link to="#" onClick={handleLogout}>Click to sign out</Link></Text>
      </div>
    </div>
  )
}

const AddActionCell = ({ flowId }) => {
  const classNames = useCustomStyles();
  const flowRoute = flowId ? `/${flowId}` : '';
  return (
    <Link className={classnames(classNames.row, classNames.addRow)} to={`/home/scheduler/new${flowRoute}`}>
      <Icon iconName='Add' />
      <span>New Action</span>
    </Link>
  )
}

const ActionCell = ({ owner, action, flowId }) => {
  const typeText = formatActionType(action.schedule);
  const email = owner.username !== action.owner_name && action.owner_name;
  const flowRoute = flowId ? `/${flowId}` : '';
  
  const history = useHistory();

  return (
    <ListItem
      title={`${action.name} ${(!action.is_active ? ' (inactive)' : '')}`}
      // title={`${action.name} ${(!action.is_active ? ' (inactive)' : '')} ${action.id}`}
      subtitle={[ email, typeText ]}
      showLinkChevron={true}
      styles={{ root: { cursor: 'pointer'} }}
      onClick={() => history.push(`/home/scheduler/${action.id}${flowRoute}`)}
    />
  );
}

function formatMonthlySchedule(month) {
  if (month.type === 'day') {
    return `${month.position} day of the month`
  }
  if (month.type === 'weekday') {
    return `${month.position} ${month.weekday}`
  }
}

export function formatActionType(schedule) {
  const scheduleLocal = scheduleToLocal(schedule);
  if (scheduleLocal.type === SCHEDULE_HOURLY) {
    return `Hourly at ${scheduleLocal.minute.toString().padStart(2, '0')} minutes`;
  }

  if (scheduleLocal.type === SCHEDULE_DAILY) {
    return `Daily at ${scheduleLocal.time}`;
  }

  if (scheduleLocal.type === SCHEDULE_MONTHLY) {
    return `Monthly at ${scheduleLocal.time} every ${formatMonthlySchedule(scheduleLocal.month)}`
  }

  if (scheduleLocal.type === SCHEDULE_MANUALLY) {
    return `Manually from Salesforce ${scheduleLocal.salesForceObject}`;
  }
  return `Weekly on ${scheduleLocal.weekday.join(', ')} at ${scheduleLocal.time}`;
}

export default inject('userStore', 'dataStore')(observer(ActionList));